import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'
import { PrimaryButton } from '../../components/Button'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalProps } from '../../components/Modal'
import Radom from '../../state/Radom'
import User from '../../state/User'
import { errorToast, successToast } from '../../util/Util'

type AccountModalProps = ModalProps & {
  onAccountCreated: () => void
}

enum FiatAccountType {
  USD = 'USD',
  EUR = 'EUR',
}

const CreateDepositAccountModal = observer(({ ...props }: AccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType>()

  const [isLoading, setIsLoading] = useState(false)

  const createFiatAccount = async (): Promise<void> => {
    if (!fiatAccountType) {
      errorToast('')
      return
    }

    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            currency: fiatAccountType
          }
        }
      })
      props.onAccountCreated()
      successToast('Deposit account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  return <Modal {...props} innerStyles={{ width: 550 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 10,
      padding: '0px 30px'
    }}>
      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 20 }}>
        <TextInputWithLabel
          label='Account nickname'
          placeholder='Enter a nickname for this account'
          disabled={isLoading}
          required
          value={accountName}
          onChange={e => {
            const el = (e.target as HTMLInputElement)
            const value = el.value
            setAccountName(value)
          }}
        />

        <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
          <InputLabel>Account type</InputLabel>
          <Dropdown
            key={fiatAccountType}
            disabled={isLoading}
            selectedContent={
              fiatAccountType
                ? <div style={{
                  fontSize: 14,
                  alignItems: 'center',
                  display: 'flex',
                  gap: 10
                }}>
                  <img
                    style={{
                      width: 20,
                      maxHeight: 20,
                      maxWidth: 20
                    }}
                    src={`https://pay.radom.com/flags/${fiatAccountType === FiatAccountType.USD ? 'us' : 'eu'}.svg`}
                  />
                  {fiatAccountType}
                </div>
                : <span style={{ fontSize: 14 }}>Select bank account type</span>
            }
            dropdownContent={
              [FiatAccountType.USD, FiatAccountType.EUR].map(c =>
                <DropdownItem
                  key={c}
                  onClick={() => {
                    setFiatAccountType(c)
                  }}
                  style={{
                    fontSize: 14,
                    alignItems: 'center',
                    display: 'flex',
                    gap: 10
                  }}>
                  <img
                    style={{
                      width: 20,
                      maxHeight: 20,
                      maxWidth: 20
                    }}
                    src={`https://pay.radom.com/flags/${c === FiatAccountType.USD ? 'us' : 'eu'}.svg`}
                  />

                  {c}
                </DropdownItem>
              )
            } />
        </div>
      </div>
    </div>

    {
      User.organization?.verificationStatus === 'Verified' &&
      <div style={{
        padding: 30,
        borderRadius: 15,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(transparent, white 20%)'
      }}>
        <PrimaryButton
          disabled={!accountName || !fiatAccountType}
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createFiatAccount()
          }}>
          Create account
        </PrimaryButton>
      </div>
    }
  </Modal>
})

export default CreateDepositAccountModal
